import { memo, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import PlayIcon from '../../../core/components/icons/PlayIcon';
import type {
    ProductMediaItem,
    ProductVideoItem,
    RenditionUrl,
} from '../../types';
import HorizontalLayout from '../../../core/components/layout/HorizontalLayout';
import VerticalLayout from '../../../core/components/layout/VerticalLayout';
import useLoginState from '../../../core/hooks/useLoginState';
import PreorderModeContext from '../../../preorder/PreorderModeContext';
import ProductImage from '../ProductImage/ProductImage';
import ProductPlaceholder from '../ProductImage/ProductPlaceholder';
import ConsentRestriction from '../../../cookies/components/ConsentRestriction';
import ProductDetailsGalleryModal from './ProductDetailsGalleryModal';
import styles from './ProductDetailsMedia.module.scss';

const SaleTag = memo(({ onSale }: { onSale: boolean }) => {
    const isLoggedIn = useLoginState();
    const { isInPreorderMode } = useContext(PreorderModeContext);
    if (isLoggedIn && onSale && !isInPreorderMode) {
        return (
            <div
                className={clsx(styles.indicator, styles.saleIndicator)}
                data-test-class="saleTag"
            >
                SALE
            </div>
        );
    }

    return null;
});

export interface MediaItem {
    url?: string;
    renditionUrl?: RenditionUrl;
    component?: ReactNode;
    isSimilarImage?: boolean;
    mediaType: 'IMAGE' | 'VIDEO';
}

const combineMedia = (
    media: ProductMediaItem[],
    videos: ProductVideoItem[] | null
) => {
    const mediaItems: MediaItem[] = [];

    const hasOnlySimilarImages = media.every(
        item => item.type === 'similarImage'
    );

    if (hasOnlySimilarImages) {
        mediaItems.push(
            ...media.map(mediaItem => ({
                ...mediaItem,
                isSimilarImage: true,
                mediaType: 'IMAGE' as MediaItem['mediaType'],
            }))
        );
    } else {
        mediaItems.push(
            ...media
                .filter(item => item.type !== 'similarImage')
                .map(mediaItem => ({
                    ...mediaItem,
                    mediaType: 'IMAGE' as MediaItem['mediaType'],
                }))
        );
    }
    if (videos) {
        mediaItems.push(
            ...videos.map(video => ({
                url: video.embedUrl,
                mediaType: 'VIDEO' as MediaItem['mediaType'],
            }))
        );
    }

    return mediaItems;
};

const CombinedMediaItem = memo(
    ({
        mediaItem: { url, mediaType, renditionUrl },
        isActive,
        updateActiveItem,
        openGallery,
    }: {
        mediaItem: MediaItem;
        isActive: boolean;
        updateActiveItem(): void;
        openGallery(): void;
    }) => {
        return (
            <div
                key={url}
                role="button"
                tabIndex={0}
                onMouseOver={updateActiveItem}
                onFocus={updateActiveItem}
                onClick={() => {
                    updateActiveItem();
                    openGallery();
                }}
                onKeyPress={e => {
                    if ([' ', 'Enter'].includes(e.key)) {
                        updateActiveItem();
                        openGallery();
                    }
                }}
                className={clsx(styles.previewImageItem, {
                    [styles.active]: isActive,
                })}
            >
                {mediaType === 'IMAGE' && (
                    <ProductImage size="60px" url={renditionUrl} />
                )}
                {mediaType === 'VIDEO' && (
                    <div className={styles.videoItemThumbnail}>
                        <PlayIcon color="#fff" />
                    </div>
                )}
            </div>
        );
    }
);

const ProductDetailsActiveMediaItem = memo(
    ({
        activeItem,
        openGallery,
        isGalleryOpen,
        showPlaceholder = false,
    }: {
        activeItem?: MediaItem;
        isGalleryOpen: boolean;
        showPlaceholder?: boolean;
        openGallery(): void;
    }) => {
        return (
            <div
                className={clsx(styles.activeImageContainer)}
                role="button"
                tabIndex={0}
                onClick={() => openGallery()}
                onKeyPress={e => {
                    if ([' ', 'Enter'].includes(e.key)) {
                        openGallery();
                    }
                }}
            >
                {showPlaceholder && <ProductPlaceholder />}
                {activeItem?.mediaType === 'IMAGE' && (
                    <ProductImage
                        renditionSize="medium"
                        url={activeItem.renditionUrl}
                        isSimilarImage={activeItem.isSimilarImage}
                    />
                )}
                {activeItem?.mediaType === 'VIDEO' && !isGalleryOpen && (
                    <div className={styles.videoEmbedContainer}>
                        <ConsentRestriction
                            className={styles.videoEmbed}
                            consent={{
                                functional: true,
                            }}
                        >
                            <iframe
                                className={styles.videoEmbed}
                                title="product video"
                                src={`${activeItem.url}`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            />
                        </ConsentRestriction>
                    </div>
                )}
            </div>
        );
    }
);

const ProductDetailsMedia = ({
    disabledActions = false,
    title,
    isNew,
    onSale,
    media = [],
    videos,
}: {
    disabledActions?: boolean;
    title: string;
    isNew: boolean;
    onSale: boolean;
    media: ProductMediaItem[];
    videos: ProductVideoItem[] | null;
}) => {
    const [activeItem, setActiveItem] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    const openGallery = () => {
        if (!media.length || disabledActions) {
            return;
        }
        setIsGalleryOpen(true);
    };

    const addKeyboardEventHandler =
        (callback: () => void) =>
        (event: React.KeyboardEvent<HTMLImageElement>) => {
            if (event.key === ' ' || event.key === 'Enter') {
                callback();
            }
        };

    const combinedMedia = combineMedia(media, videos);

    return (
        <div className={styles.productDetailsGallery}>
            <HorizontalLayout gap="small">
                {!disabledActions && (
                    <VerticalLayout stretchChildren={false}>
                        {combinedMedia.slice(0, 5).map((mediaItem, index) => (
                            <CombinedMediaItem
                                key={mediaItem.url}
                                mediaItem={mediaItem}
                                isActive={activeItem === index}
                                updateActiveItem={() => setActiveItem(index)}
                                openGallery={openGallery}
                            />
                        ))}
                    </VerticalLayout>
                )}
                <ProductDetailsActiveMediaItem
                    activeItem={combinedMedia[activeItem]}
                    openGallery={openGallery}
                    isGalleryOpen={isGalleryOpen}
                    showPlaceholder={combinedMedia.length === 0}
                />
            </HorizontalLayout>
            {isNew && (
                <div className={clsx(styles.indicator, styles.newIndicator)}>
                    <FormattedMessage id="new" />
                </div>
            )}
            <SaleTag onSale={onSale} />
            {!disabledActions && combinedMedia.length > 5 && (
                <div
                    role="button"
                    tabIndex={0}
                    className={styles.moreMediaButton}
                    onClick={() => openGallery()}
                    onKeyPress={addKeyboardEventHandler(() => openGallery())}
                >
                    +&nbsp;
                    <FormattedMessage id="more pictures" />
                </div>
            )}
            {isGalleryOpen && (
                <ProductDetailsGalleryModal
                    title={title}
                    onClose={() => setIsGalleryOpen(false)}
                    media={combinedMedia}
                    activeItem={activeItem}
                    setActiveItem={setActiveItem}
                />
            )}
        </div>
    );
};

export default memo(ProductDetailsMedia);
